import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  OnChanges,
  SimpleChanges,
  AfterViewInit,
  AfterViewChecked,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
} from '@angular/core';
import { FeedbackService } from 'src/app/services/feedback/feedback.service';
import { BusinessServiceService } from 'src/app/services/business-service/business-service.service';
import { FeedbackEntity } from 'src/app/models/feedback.model';
import { ServiceComboEntity } from 'src/app/models/service-combo.model';
import { Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';

declare var Swiper: any;

@Component({
  selector: 'app-restaurantleftsidebar',
  templateUrl: './restaurantleftsidebar.component.html',
  styleUrls: ['./restaurantleftsidebar.component.css'],
})
export class RestaurantleftsidebarComponent
  implements OnInit, OnDestroy, OnChanges, AfterViewInit, AfterViewChecked
{
  @Input() storeId: string | null = null;
  @ViewChild('swiperContainer', { static: false }) swiperContainer!: ElementRef;

  topCustomerReviews: FeedbackEntity[] = [];
  isLoadingReviews = false;
  private serviceCombosMap = new Map<string, ServiceComboEntity>();
  private subscriptions = new Subscription();
  private swiperInstance: any = null;
  private swiperInitialized = false;

  constructor(
    private feedbackService: FeedbackService,
    private businessServiceService: BusinessServiceService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (this.storeId) {
      this.loadStoreServices(this.storeId);
      this.loadTopCustomerReviews(this.storeId);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['storeId'] &&
      changes['storeId'].currentValue &&
      !changes['storeId'].firstChange
    ) {
      const storeId = changes['storeId'].currentValue;
      this.loadStoreServices(storeId);
      this.loadTopCustomerReviews(storeId);
    }
  }

  ngAfterViewInit(): void {
    // Initialize Swiper after view is initialized
    setTimeout(() => {
      this.initializeSwiper();
    }, 100);
  }

  ngAfterViewChecked(): void {
    // Reinitialize Swiper if reviews are loaded and Swiper hasn't been initialized yet
    if (
      !this.isLoadingReviews &&
      this.topCustomerReviews.length > 0 &&
      !this.swiperInitialized
    ) {
      setTimeout(() => {
        this.initializeSwiper();
      }, 100);
    }
  }

  ngOnDestroy(): void {
    // Clean up Swiper instance
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }
    this.swiperInitialized = false;
    this.subscriptions.unsubscribe();
  }

  loadStoreServices(storeId: string): void {
    this.subscriptions.add(
      this.businessServiceService
        .getCombosForStore(storeId)
        .pipe(
          catchError((error) => {
            console.error('Error loading store services for sidebar:', error);
            return of([]);
          })
        )
        .subscribe((services) => {
          // Create a map of service combos by ID for quick lookup
          this.serviceCombosMap.clear();
          services.forEach((service) => {
            this.serviceCombosMap.set(service.id, service);
          });
        })
    );
  }

  loadTopCustomerReviews(storeId: string): void {
    this.isLoadingReviews = true;
    this.subscriptions.add(
      this.feedbackService
        .getStoreFeedback(storeId)
        .pipe(
          catchError((error) => {
            console.error('Error loading feedbacks for sidebar:', error);
            this.isLoadingReviews = false;
            return of([]);
          })
        )
        .subscribe((feedbacks) => {
          this.topCustomerReviews =
            this.getTopCustomersLatestReviews(feedbacks);
          this.isLoadingReviews = false;
          // Reset initialization flag and reinitialize Swiper after reviews are loaded
          this.swiperInitialized = false;
          if (this.topCustomerReviews.length > 0) {
            setTimeout(() => {
              this.initializeSwiper();
            }, 100);
          }
        })
    );
  }

  /**
   * Get top 2 customers by feedback count, then get their latest review with comment
   */
  private getTopCustomersLatestReviews(
    feedbacks: FeedbackEntity[]
  ): FeedbackEntity[] {
    if (!feedbacks || feedbacks.length === 0) {
      return [];
    }

    // Filter to only include feedbacks with comments
    const feedbacksWithComments = feedbacks.filter((feedback) => {
      return feedback.comment && feedback.comment.trim().length > 0;
    });

    if (feedbacksWithComments.length === 0) {
      return [];
    }

    // Count feedbacks per customer (only those with comments)
    const customerFeedbackCount = new Map<string, number>();
    const customerFeedbacks = new Map<string, FeedbackEntity[]>();

    feedbacksWithComments.forEach((feedback) => {
      const customerId = feedback.customerId;

      // Count feedbacks
      const count = customerFeedbackCount.get(customerId) || 0;
      customerFeedbackCount.set(customerId, count + 1);

      // Group feedbacks by customer
      if (!customerFeedbacks.has(customerId)) {
        customerFeedbacks.set(customerId, []);
      }
      customerFeedbacks.get(customerId)!.push(feedback);
    });

    // Sort customers by feedback count (descending) and take top 2
    const sortedCustomers = Array.from(customerFeedbackCount.entries())
      .sort((a, b) => b[1] - a[1]) // Sort by count descending
      .slice(0, 2) // Take top 2
      .map(([customerId]) => customerId);

    // Get latest review for each top customer
    const topReviews: FeedbackEntity[] = [];

    sortedCustomers.forEach((customerId) => {
      const customerFeedbackList = customerFeedbacks.get(customerId) || [];

      if (customerFeedbackList.length > 0) {
        // Sort by date and get the latest
        const latestFeedback = customerFeedbackList.sort((a, b) => {
          const dateA = this.getFeedbackDate(a.createdTime);
          const dateB = this.getFeedbackDate(b.createdTime);
          return dateB.getTime() - dateA.getTime(); // Descending
        })[0];

        topReviews.push(latestFeedback);
      }
    });

    return topReviews;
  }

  /**
   * Safely convert feedback date to Date object
   * Handles Firestore Timestamp objects with _seconds and _nanoseconds
   */
  private getFeedbackDate(date: Date | string | null | undefined | any): Date {
    if (!date) {
      return new Date(0);
    }

    // If it's already a Date object
    if (date instanceof Date) {
      return isNaN(date.getTime()) ? new Date(0) : date;
    }

    // If it's a Firestore Timestamp (has toDate method)
    if (date && typeof date.toDate === 'function') {
      return date.toDate();
    }

    // If it's a Firestore Timestamp (has _seconds property with underscore)
    if (date && typeof date._seconds === 'number') {
      return new Date(date._seconds * 1000);
    }

    // If it's a Firestore Timestamp (has seconds property without underscore)
    if (date && typeof date.seconds === 'number') {
      return new Date(date.seconds * 1000);
    }

    // If it's a string or number
    if (typeof date === 'string' || typeof date === 'number') {
      const parsed = new Date(date);
      return isNaN(parsed.getTime()) ? new Date(0) : parsed;
    }

    // Fallback: try to convert
    const parsed = new Date(date as any);
    return isNaN(parsed.getTime()) ? new Date(0) : parsed;
  }

  formatFeedbackDate(date: Date | string | null | undefined | any): string {
    if (!date) {
      return '';
    }

    let feedbackDate: Date;

    // If it's already a Date object
    if (date instanceof Date) {
      feedbackDate = date;
    }
    // If it's a Firestore Timestamp (has toDate method)
    else if (date && typeof date.toDate === 'function') {
      feedbackDate = date.toDate();
    }
    // If it's a Firestore Timestamp (has _seconds property with underscore)
    else if (date && typeof date._seconds === 'number') {
      feedbackDate = new Date(date._seconds * 1000);
    }
    // If it's a Firestore Timestamp (has seconds property without underscore)
    else if (date && typeof date.seconds === 'number') {
      feedbackDate = new Date(date.seconds * 1000);
    }
    // If it's a string or number
    else if (typeof date === 'string' || typeof date === 'number') {
      feedbackDate = new Date(date);
    }
    // Fallback: try to convert
    else {
      feedbackDate = new Date(date as any);
    }

    if (isNaN(feedbackDate.getTime())) {
      return '';
    }

    return feedbackDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  getFeedbackRating(feedback: FeedbackEntity): number {
    return feedback.hasHeart ? 5 : 4;
  }

  getFeedbackCommentPreview(comment: string | undefined): string {
    if (!comment) {
      return 'No comment provided.';
    }
    // Remove HTML tags and get first 50 characters
    const text = comment.replace(/<[^>]*>/g, '');
    return text.length > 50 ? text.substring(0, 50) + '...' : text;
  }

  /**
   * Get the first letter of customer name for avatar display
   */
  getCustomerInitial(customerName: string | undefined | null): string {
    if (!customerName || customerName.trim().length === 0) {
      return '?';
    }
    return customerName.trim().charAt(0).toUpperCase();
  }

  /**
   * Check if customer photo URL exists and is valid
   */
  hasCustomerPhoto(photoUrl: string | undefined | null): boolean {
    return !!photoUrl && photoUrl.trim().length > 0;
  }

  /**
   * Get the first service combo's photo URL from the appointment
   * Maps appointment service combo IDs with store services to get the photo
   */
  getFirstServiceComboPhoto(feedback: FeedbackEntity): string {
    if (!feedback.appointment) {
      return 'assets/img/nails-fallback.jpg';
    }

    const appointment = feedback.appointment as any;
    let firstComboId: string | null = null;

    // Check if appointment has serviceCombos array (from backend response)
    if (
      appointment.serviceCombos &&
      Array.isArray(appointment.serviceCombos) &&
      appointment.serviceCombos.length > 0
    ) {
      firstComboId = appointment.serviceCombos[0]?.id || null;
    }
    // Check if appointment has services Record structure
    else if (appointment.services && typeof appointment.services === 'object') {
      const services = appointment.services;
      const firstServiceKey = Object.keys(services)[0];
      if (firstServiceKey && services[firstServiceKey]?.combos?.length > 0) {
        firstComboId = services[firstServiceKey].combos[0]?.id || null;
      }
    }

    // If we found a combo ID, look it up in the store services map
    if (firstComboId && this.serviceCombosMap.has(firstComboId)) {
      const serviceCombo = this.serviceCombosMap.get(firstComboId);
      if (serviceCombo && serviceCombo.photoUrl) {
        return serviceCombo.photoUrl;
      }
    }

    return 'assets/img/nails-fallback.jpg';
  }

  /**
   * Initialize Swiper slider
   */
  private initializeSwiper(): void {
    if (!this.swiperContainer || !this.swiperContainer.nativeElement) {
      return;
    }

    // Only initialize if we have reviews to show
    if (this.topCustomerReviews.length === 0) {
      return;
    }

    // Destroy existing Swiper instance if it exists
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }

    // Initialize new Swiper instance
    if (typeof Swiper !== 'undefined') {
      try {
        this.swiperInstance = new Swiper(
          this.swiperContainer.nativeElement,
          {
            slidesPerView: 1,
            spaceBetween: 0,
            loop: this.topCustomerReviews.length > 1,
            navigation: {
              nextEl: this.swiperContainer.nativeElement.querySelector(
                '.swiper-button-next'
              ),
              prevEl: this.swiperContainer.nativeElement.querySelector(
                '.swiper-button-prev'
              ),
            },
            autoplay: this.topCustomerReviews.length > 1 ? {
              delay: 3000,
              disableOnInteraction: false,
            } : false,
          }
        );
        this.swiperInitialized = true;
      } catch (error) {
        console.error('Error initializing Swiper:', error);
      }
    } else {
      console.warn('Swiper is not defined. Make sure Swiper library is loaded.');
    }
  }
}
